/**
 * Best-effort curator/brand name derived from a vault's display name, for the
 * providers whose data source gives only a curator *address* (no name):
 * Lista (Moolah on-chain) and Euler-Earn (subgraph). Curated-vault names are
 * conventionally `<Curator> [x <Protocol>] <Asset> [Vault|Savings|…]`
 * (e.g. `Gauntlet USDT Vault`, `MEV Capital USDC`, `RockawayX PT Yield`), so
 * the leading run of words up to the asset symbol / a structural word is the
 * curator.
 *
 * Heuristic and intentionally conservative — returns `undefined` rather than a
 * guess when nothing plausible leads the name. Never use it to override a
 * curator name that the provider's API supplies directly (Morpho, Lagoon).
 */

/** Structural / protocol words that terminate the curator run. */
const STOP_WORDS = new Set([
  'vault',
  'vaults',
  'savings',
  'yield',
  'exclusive',
  'dao',
  'x',
  'pt',
  'prime',
  'staked',
  'core',
  'rwa',
  'lista', // protocol brand; a bare "Lista …" vault has no distinct curator
  'moolah',
])

/** Common asset tickers that appear in names (terminate the curator run even
 *  when they differ from the vault's own `asset.symbol`). */
const ASSET_TOKENS = new Set([
  'usdc',
  'usdt',
  'usd1',
  'usds',
  'dai',
  'usde',
  'susde',
  'lisusd',
  'gho',
  'frax',
  'pyusd',
  'rusd',
  'usr',
  'rlp',
  'eth',
  'weth',
  'wsteth',
  'reth',
  'steth',
  'cbeth',
  'weeth',
  'ezeth',
  'btc',
  'wbtc',
  'btcb',
  'cbbtc',
  'tbtc',
  'xaut',
  'xrp',
  'bnb',
  'wbnb',
  'sol',
  'op',
  'arb',
  'avax',
  'pol',
  'matic',
  'sei',
  'celo',
  'hype',
  'plume',
  's',
])

/** Asset-ish token: listed, usd-bearing, or a wrapped form (cbXRP, wstETH). */
const isAssetToken = (tl: string, asset: string): boolean => {
  if ((asset && tl === asset) || ASSET_TOKENS.has(tl) || tl.includes('usd')) {
    return true
  }
  const unwrapped = tl.replace(/^(cb|wst|ws|w|st)/, '')
  return unwrapped !== tl && ASSET_TOKENS.has(unwrapped)
}

/**
 * Derive a curator name from a vault display name, or `undefined` when none is
 * plausible. `assetSymbol` (the vault's underlying ticker) is an extra stop
 * token so e.g. `Foo BAR Vault` with asset `BAR` yields `Foo`.
 */
export function curatorNameFromVaultName(
  name: string | null | undefined,
  assetSymbol?: string,
): string | undefined {
  if (!name) return undefined
  // A test/fake vault's leading word is not a curator, whatever the casing —
  // without this, chain-56 Euler-Earn served a vault curated by "TEST".
  if (/\b(test|testing|fake)\b/i.test(name)) return undefined

  // "<product> by <curator>" states the curator outright — take the suffix
  // ("cyETH by CurveYield" → "CurveYield").
  const by = /\bby\s+(.{2,})$/i.exec(name)
  if (by) {
    const suffix = by[1].trim().split(/\s+/).slice(0, 3).join(' ')
    if (
      suffix.length >= 2 &&
      !isAssetToken(suffix.toLowerCase(), (assetSymbol ?? '').toLowerCase())
    ) {
      return suffix
    }
  }

  // Drop a trailing parenthetical, e.g. `… Vault(pUSDT)`; text before a colon
  // is a PRODUCT title ("CurveYield Max: crvUSD") — the curator run must
  // resolve inside it or not at all.
  const cleaned = name
    .replace(/\([^)]*\)\s*$/, '')
    .split(':')[0]
    .trim()
  if (!cleaned) return undefined

  const tokens = cleaned
    .split(/[\s/]+/)
    .flatMap((t) => t.split('-'))
    .filter(Boolean)

  const asset = (assetSymbol ?? '').toLowerCase()
  const out: string[] = []
  let terminated = false
  for (const tok of tokens) {
    const tl = tok.toLowerCase()
    // `isAssetToken` also counts usd-bearing tickers ("fxUSD", "ctUSD") and
    // wrapped forms (cbXRP) the explicit list and the vault's own asset
    // symbol both miss — otherwise "fxUSD universal vault" reads as curator
    // "fxUSD universal".
    if (STOP_WORDS.has(tl) || isAssetToken(tl, asset)) {
      terminated = true
      break
    }
    out.push(tok)
    if (out.length >= 3) break // cap multi-word curators (e.g. "MEV Capital")
  }

  // A run that never hit the vault-name structure — a stop word, the asset —
  // swallowed the name whole ("RHVault", a bare brand). That is the vault's
  // NAME, not its curator; deriving a curator from it would just duplicate it.
  if (!terminated || out.length === tokens.length) return undefined

  const curator = out.join(' ').trim()
  return curator.length >= 2 ? curator : undefined
}
